import React, { FC, useContext, useEffect } from 'react';
import { formatDate } from 'utils/format';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ScannerCase, ScannerFileProcessingStatus, ScanSource } from 'models/scanner-case';
import InfoText from 'components/shared/info-text.component';
import { socketIo } from 'context/socket';
import { currentUserVar } from 'apollo/reactive-variables/user';
import DocumentViewer from 'components/shared/document-viewer/document-viewer.component';

type ScannerCaseSidebarProps = {
    scannerCase: ScannerCase;
};
const ScannerCaseSidebar: FC<ScannerCaseSidebarProps> = ({ scannerCase }: ScannerCaseSidebarProps) => {
    const { t: translate } = useTranslation(['scan-cases', 'common', 'orders']);
    const socket = useContext(socketIo.getSocketContext());
    const currentUser = currentUserVar();
    const [isProcessing, setIsProcessing] = React.useState(
        scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.NOT_STARTED ||
            scannerCase.fileProcessingStatus === ScannerFileProcessingStatus.ERROR,
    );

    const getPatientName = () => {
        return scannerCase.patient?.firstName
            ? `${scannerCase.patient?.firstName} ${scannerCase.patient?.lastName || ''}`
            : translate('scan-cases:NO_PATIENT_NAME');
    };

    useEffect(() => {
        const handleFilesProcessComplete = (event: {
            data: { userId: string; scannerCaseId: string; status: ScannerFileProcessingStatus };
        }) => {
            if (event.data.userId === currentUser._id && event.data.scannerCaseId === scannerCase._id) {
                if (event.data.status === ScannerFileProcessingStatus.COMPLETED) {
                    setIsProcessing(false);
                }
            }
        };

        socket.on('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        return () => {
            // Remove only this listener — removeAllListeners would also detach the
            // handlers other scanner case components register for this same event.
            socket.off('SCANNER_CASE_FILES_PROCESS_COMPLETE', handleFilesProcessComplete);
        };
    }, [socket]);

    if (scannerCase.source === ScanSource.THREE_SHAPE && scannerCase.printableOrderForm) {
        // Shrink-wrap the viewer so the sidebar always matches the rendered
        // document width (contentWidth + the viewer's frame) instead of a
        // fixed width that must be kept in sync manually.
        return (
            <Box width="fit-content">
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="h4">{translate('common:CLINIC')}:</Typography>
                    <Box data-test="printable-order-form-viewer.clinic-name">
                        {scannerCase.clinic?.name || translate('scan-cases:MISSING_CLINIC_NAME')}
                    </Box>
                </Box>
                <DocumentViewer
                    url={scannerCase.printableOrderForm.file.url}
                    contentType={scannerCase.printableOrderForm.file.mimetype}
                    filename={scannerCase.printableOrderForm.file.filename}
                    height="80vh"
                    contentWidth={730}
                    htmlZoom={0.75}
                    testIdPrefix="printable-order-form-viewer"
                />
            </Box>
        );
    }

    return (
        <Box width="450px">
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box mb={2}>
                    <Typography variant="h3">{translate(`scan-cases:${scannerCase.source}_INFO`)}</Typography>
                </Box>

                <Box mb={0.5}>
                    <Typography variant="h4">{scannerCase.displayCaseId}</Typography>
                </Box>

                {scannerCase.deliveryDate ? (
                    <Box mb={2}>
                        {translate('orders:DELIVERY_DATE')} {formatDate(new Date(scannerCase.deliveryDate), 'datetime')}
                    </Box>
                ) : undefined}

                <Box mb={2}>
                    <Box>
                        <Typography variant="h4">{translate('common:DENTIST')}</Typography>
                    </Box>
                    <Box>{scannerCase.dentist?.name || translate('orders:UNKNOWN')}</Box>
                    <Box>{scannerCase.clinic?.name || translate('orders:UNKNOWN')}</Box>
                </Box>

                <Box mb={2}>
                    <Box>
                        <Typography variant="h4">{translate('common:PATIENT')}</Typography>
                    </Box>
                    <Box>{getPatientName()}</Box>
                    <Box>{scannerCase.patient?.patientRefNo || ''}</Box>
                </Box>

                <Box>
                    <Box>
                        <Typography variant="h4">{translate('orders:COMMENTS')}</Typography>
                    </Box>
                    <Box>{scannerCase.comments || <InfoText text={translate('orders:NO_COMMENTS_AVAILABLE')} />}</Box>
                </Box>
            </Paper>
            {/* <Box mt={1}>
                {isProcessing ? (
                    <Alert severity="info">{translate('scan-cases:PROCESSING_FILES_INFO')}</Alert>
                ) : (
                    <Alert severity="success">{translate('scan-cases:PROCESSING_FILES_COMPLETED_INFO')}</Alert>
                )}
            </Box> */}
        </Box>
    );
};

export default ScannerCaseSidebar;
